/**
 * SupplyChainSuppliersPanel — SCM4 Suppliers tab + SCM13 compliance filters.
 *
 * Filtered directory of suppliers shortlisted for the current site. Each
 * card shows reliability tier, HQ, supported categories, and the
 * certification chips that the supplier carries. SCM14 adds a coloured
 * overall-risk pill next to the reliability-tier pill. Clicking the
 * risk pill opens a popover listing the three component reasons
 * (geopolitical, cert expiry, financial health); clicking elsewhere or
 * pressing Escape closes it. Quote history wiring still belongs to
 * SCM-Integration.
 *
 * SCM13 additions:
 *   - Chip strip at the top with the most-tagged certifications in the
 *     current shortlist. Multi-select is AND-semantic — picking two chips
 *     narrows to suppliers that carry both.
 *   - "Match BoM requirements" preset → selects every cert listed in any
 *     BoM item, so the directory view collapses to the suppliers that
 *     would actually qualify for the open BoM.
 *   - `initialFilter` prop lets the parent panel pre-apply a filter when
 *     the user navigates here from a BoM row's "N matching suppliers" link.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, Link2, Plus, Search, ShieldCheck, X } from 'lucide-react';
import type {
  ComplianceCertificationId,
  EquipmentCatalogEntry,
  SCMCategory,
  Supplier,
  SupplierRiskAssessment,
  SupplyChainPlan,
} from '../../../types/supplyChain';
import { SCM_CATEGORY_LABELS, normaliseCertification } from '../../../types/supplyChain';
import {
  bomRequirementSet,
  certificationCounts,
  normalizeRequirements,
  supplierEquipmentMatchEvidence,
  supplierHasAll,
  suppliersMatchingBomItem,
} from '../../../lib/supplierMatching';
import {
  getComplianceCertificationById,
  normalizeComplianceId,
} from '../../../lib/supplyChainBaselines';
import { resolveSupplierDirectory } from '../../../lib/resolveSupplierDirectory';
import { resolveEquipmentCatalog } from '../../../lib/resolveEquipmentCatalog';
import { cn } from '../../../lib/utils';
import {
  CERT_EXPIRY_WINDOW_DAYS,
  supplierRiskAssessment,
} from '../../../lib/supplyChainRisks';
import { SupplierPartnershipBadge, SupplierStaleBadge } from './SupplierPartnershipBadge';
import { ScmDataDisclaimer } from './ScmDataDisclaimer';
import { getReactorVendor } from '../../../config/reactorVendorCatalog';

export interface SupplyChainSuppliersPanelProps {
  plan: SupplyChainPlan | undefined;
  companyId?: string | null;
  /** Pre-applied compliance filter (taxonomy ids). Used when navigating
   *  from a BoM row's "matching suppliers" link. */
  initialFilter?: readonly ComplianceCertificationId[];
  /** Exact current BOM identity carried by the equipment row; no free-text inference. */
  initialBomItemId?: string;
  /** SCM14 — override "now" for deterministic test output / time-travel UI.
   *  Accepts a Date or ISO string. Falls back to the current time. */
  now?: Date | string;
  /** Site reactor vendor id — highlights linked suppliers (v2). */
  reactorVendorId?: string;
  onAddSupplier?: (supplier: Pick<Supplier, 'name' | 'hq' | 'categories'>) => void | Promise<void>;
  onAddFromDirectory?: (supplierId: string) => void | Promise<void>;
  onAttachSupplierToBomItem?: (args: { supplierId: string; bomItemId: string }) => void | Promise<void>;
  onEmailSupplier?: (supplier: Supplier) => void;
}

type SupplierSubView = 'project' | 'directory';

/** Chips with at least this many suppliers behind them are pinned to the
 *  visible strip; the rest are revealed under a "Show all" toggle. Spec
 *  asked for >5 tagged suppliers but in practice we keep an aggressive
 *  default and let the user expand. */
const PRIMARY_CHIP_THRESHOLD = 5;

/** Certs we always surface near the front of the chip strip when present.
 *  Order is mirrored in the visible row so NQA-1 leads on nuclear sites. */
const PRIORITY_IDS: readonly ComplianceCertificationId[] = [
  'nqa-1',
  'asme-n-stamp',
  'asme-section-iii',
  'iso-9001-2015',
  // FEMA-listed is an aspirational chip for civil-construction — we don't
  // have a dedicated taxonomy entry yet, but the priority order keeps the
  // visible strip stable.
];

function tierTone(tier: Supplier['reliabilityTier']): string {
  switch (tier) {
    case 'tier-1':
      return 'bg-success/15 text-success border-success/30';
    case 'tier-2':
      return 'bg-primary/15 text-primary border-primary/30';
    case 'tier-3':
      return 'bg-warning/15 text-warning border-warning/30';
    default:
      return 'bg-muted text-text-secondary border-border';
  }
}

/** Visual tone for the SCM14 overall-risk pill. */
function riskTone(level: SupplierRiskAssessment['overallTier']): string {
  switch (level) {
    case 'low':
      return 'bg-success/15 text-success border-success/30';
    case 'medium':
      return 'bg-warning/15 text-warning border-warning/30';
    case 'high':
      return 'bg-warning/25 text-warning border-warning/50';
    case 'critical':
      return 'bg-danger/15 text-danger border-danger/40';
  }
}

/** Human-readable label for the geopolitical component line. */
function geopoliticalLine(a: SupplierRiskAssessment, hqCountry: string): string {
  const base = `Geopolitical — ${a.geopoliticalRiskLevel}`;
  if (!a.geopoliticalMatched) return `${base} (unmapped: ${hqCountry || 'n/a'} — defaults to medium pending review)`;
  return `${base} (HQ ${hqCountry})`;
}

/** Human-readable label for the cert-expiry component line. */
function certLine(a: SupplierRiskAssessment): string {
  if (a.certsExpiredCount > 0) {
    const s = a.certsExpiredCount === 1 ? '' : 's';
    return `Certifications — ${a.certsExpiredCount} expired${a.certsExpiringSoonCount > a.certsExpiredCount
      ? ` + ${a.certsExpiringSoonCount - a.certsExpiredCount} more lapsing within ${CERT_EXPIRY_WINDOW_DAYS}d`
      : ''} (cert${s} require renewal before procurement)`;
  }
  if (a.certsExpiringSoonCount > 0) {
    const s = a.certsExpiringSoonCount === 1 ? '' : 's';
    return `Certifications — ${a.certsExpiringSoonCount} expiring within ${CERT_EXPIRY_WINDOW_DAYS}d (cert${s} need renewal tracking)`;
  }
  return 'Certifications — no expiry within window';
}

/** Human-readable label for the financial-health component line. */
function financialLine(a: SupplierRiskAssessment): string {
  switch (a.financialHealthTier) {
    case 'investment-grade':
      return 'Financial health — investment-grade (public IG credit rating)';
    case 'speculative':
      return 'Financial health — speculative (high-yield / sub-IG)';
    case 'distressed':
      return 'Financial health — distressed (avoid sole-source commitments)';
    case 'unknown':
    default:
      return 'Financial health — unknown (admin curation pending; D&B integration in Phase 3)';
  }
}

/** Display label for a cert id — uses the taxonomy `name` when known and
 *  the id verbatim otherwise (preserves freeform fallback strings). */
function certLabel(id: ComplianceCertificationId): string {
  return getComplianceCertificationById(id)?.name ?? id;
}

export const SupplyChainSuppliersPanel: React.FC<SupplyChainSuppliersPanelProps> = ({
  plan,
  companyId,
  initialFilter,
  initialBomItemId,
  now,
  onAddSupplier,
  onAddFromDirectory,
  onAttachSupplierToBomItem,
  onEmailSupplier,
  reactorVendorId,
}) => {
  const vendorLinkage = reactorVendorId ? getReactorVendor(reactorVendorId) : null;
  const [subView, setSubView] = useState<SupplierSubView>('project');
  const [directory, setDirectory] = useState<Supplier[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<Set<ComplianceCertificationId>>(
    () => new Set(initialFilter ?? []),
  );
  const [showAllChips, setShowAllChips] = useState(false);
  const [openRiskPopoverId, setOpenRiskPopoverId] = useState<string | null>(null);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierCountry, setNewSupplierCountry] = useState('');
  const [newSupplierCategory, setNewSupplierCategory] = useState<SCMCategory>('power-conversion');
  const [attachBomItemId, setAttachBomItemId] = useState('');
  const [matchingBomItemId, setMatchingBomItemId] = useState(initialBomItemId ?? '');
  const [includeCategoryCandidates, setIncludeCategoryCandidates] = useState(false);
  const [equipmentCatalog, setEquipmentCatalog] = useState<EquipmentCatalogEntry[]>([]);

  useEffect(() => {
    setMatchingBomItemId(initialBomItemId ?? '');
    setIncludeCategoryCandidates(false);
    setSelectedId(null);
  }, [initialBomItemId]);

  useEffect(() => {
    setEquipmentCatalog([]);
    if (!matchingBomItemId) return;
    let cancelled = false;
    void resolveEquipmentCatalog(companyId).then(entries => {
      if (!cancelled) setEquipmentCatalog(entries);
    }).catch(() => {
      if (!cancelled) setEquipmentCatalog([]);
    });
    return () => { cancelled = true; };
  }, [companyId, matchingBomItemId]);

  const matchingBomItem = plan?.bom.find(item => item.id === matchingBomItemId);
  const projectEquipmentMatchIds = useMemo(() => new Set(
    suppliersMatchingBomItem(plan, matchingBomItem, plan?.suppliers, equipmentCatalog).map(supplier => supplier.id),
  ), [plan, matchingBomItem, equipmentCatalog]);
  const directoryEquipmentMatchIds = useMemo(() => new Set(
    suppliersMatchingBomItem(plan, matchingBomItem, directory, equipmentCatalog).map(supplier => supplier.id),
  ), [plan, matchingBomItem, directory, equipmentCatalog]);

  // If the parent re-applies an initialFilter (e.g. user clicked a BoM
  // row again with a different requirement set), reset local state.
  useEffect(() => {
    if (initialFilter) {
      setActiveFilters(new Set(initialFilter));
    }
  }, [initialFilter]);

  useEffect(() => {
    if (subView !== 'directory') return;
    let cancelled = false;
    setDirectoryLoading(true);
    void resolveSupplierDirectory(companyId).then((entries) => {
      if (!cancelled) {
        setDirectory(entries);
        setDirectoryLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [subView, companyId]);

  const shortlistedIds = useMemo(() => new Set((plan?.suppliers ?? []).map(s => s.id)), [plan?.suppliers]);

  const browseFiltered = useMemo(() => {
    const q = browseSearch.trim().toLowerCase();
    return directory.filter(supplier => {
      if (matchingBomItemId) {
        if (!matchingBomItem) return false;
        const recordedMatch = directoryEquipmentMatchIds.has(supplier.id);
        const categoryCandidate = includeCategoryCandidates
          && supplier.categories.includes(matchingBomItem.category)
          && supplierHasAll(supplier, normalizeRequirements(matchingBomItem));
        if (!recordedMatch && !categoryCandidate) return false;
        if (!supplierHasAll(supplier, Array.from(activeFilters))) return false;
      }
      return !q || `${supplier.name} ${supplier.hq.country} ${supplier.categories.join(' ')}`.toLowerCase().includes(q);
    }).slice(0, 100);
  }, [directory, browseSearch, matchingBomItemId, matchingBomItem, directoryEquipmentMatchIds, includeCategoryCandidates, activeFilters]);

  const suppliers = plan?.suppliers ?? [];

  /** id → count in current shortlist, used for chip ordering / hiding. */
  const counts = useMemo(() => certificationCounts(plan), [plan]);

  /** Set of every cert id mentioned across the BoM (for the preset). */
  const bomReqIds = useMemo(() => bomRequirementSet(plan), [plan]);

  /** Visible chips: every cert tagged on ≥1 supplier, ordered priority-first
   *  then by count descending. */
  const allChipIds = useMemo(() => {
    const all = Array.from(counts.keys());
    all.sort((a, b) => {
      const pa = PRIORITY_IDS.indexOf(a);
      const pb = PRIORITY_IDS.indexOf(b);
      if (pa !== -1 || pb !== -1) {
        // Priorities come first; -1 (not in list) sinks to the bottom.
        return (pa === -1 ? Infinity : pa) - (pb === -1 ? Infinity : pb);
      }
      const ca = counts.get(a) ?? 0;
      const cb = counts.get(b) ?? 0;
      if (ca !== cb) return cb - ca;
      return certLabel(a).localeCompare(certLabel(b));
    });
    return all;
  }, [counts]);

  const primaryChipIds = useMemo(
    () => allChipIds.filter((id) => (counts.get(id) ?? 0) >= PRIMARY_CHIP_THRESHOLD),
    [allChipIds, counts],
  );

  const visibleChipIds = showAllChips ? allChipIds : primaryChipIds;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const needed = Array.from(activeFilters);
    return suppliers.filter((s) => {
      if (matchingBomItemId && !projectEquipmentMatchIds.has(s.id)) return false;
      if (needed.length > 0 && !supplierHasAll(s, needed)) return false;
      if (q) {
        const certText = (s.certifications ?? []).map((c) => certLabel(normaliseCertification(c).id)).join(' ');
        const hay = `${s.name} ${s.hq.country} ${s.hq.city ?? ''} ${certText}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [suppliers, search, activeFilters, matchingBomItemId, projectEquipmentMatchIds]);

  // Memoise risk per visible supplier. Keyed on supplier id + the
  // serialised `now` so the popover stays stable across renders but
  // recomputes when the caller passes a different time anchor.
  const nowKey = useMemo(() => {
    if (now instanceof Date) return now.toISOString();
    if (typeof now === 'string') return now;
    return null;
  }, [now]);
  const riskById = useMemo(() => {
    const anchor = now ?? new Date();
    const map = new Map<string, SupplierRiskAssessment>();
    for (const s of filtered) map.set(s.id, supplierRiskAssessment(s, anchor));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, nowKey]);

  // Close popover on Escape.
  useEffect(() => {
    if (!openRiskPopoverId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenRiskPopoverId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openRiskPopoverId]);

  const selected = selectedId
    ? (matchingBomItemId ? filtered : suppliers).find((s) => s.id === selectedId) ?? null
    : null;
  const selectedRisk = selected
    ? riskById.get(selected.id) ?? supplierRiskAssessment(selected, now ?? new Date())
    : null;

  const toggleFilter = (id: ComplianceCertificationId) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearFilters = () => setActiveFilters(new Set());

  const applyBomPreset = () => {
    setActiveFilters(new Set(bomReqIds));
    setMatchingBomItemId('');
    setIncludeCategoryCandidates(false);
    setSelectedId(null);
  };

  const shortlistedItems = useMemo(() => {
    if (!selected || !plan) return [];
    return plan.bom.filter((b) => (b.shortlistedSupplierIds ?? []).includes(selected.id));
  }, [selected, plan]);

  const equipmentFilter = (directoryMode = false): React.JSX.Element => (
    <div className="space-y-2 border border-border px-3 py-2 text-xs" data-testid="scm-suppliers-equipment-filter">
      <label className="flex flex-wrap items-center gap-2 text-text-secondary">
        Match selected equipment
        <select
          aria-label="Match selected equipment"
          value={matchingBomItemId}
          onChange={event => {
            setMatchingBomItemId(event.target.value);
            setIncludeCategoryCandidates(false);
            setActiveFilters(new Set());
            setSelectedId(null);
            setAttachBomItemId(event.target.value);
          }}
          className="max-w-full border border-border bg-background px-2 py-1 text-text-primary"
        >
          <option value="">All equipment</option>
          {matchingBomItemId && !matchingBomItem && <option value={matchingBomItemId}>Selected equipment is no longer available</option>}
          {(plan?.bom ?? []).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      {matchingBomItem && <p className="max-w-3xl text-text-muted">Recorded product, OEM, quote or item links must satisfy this equipment’s certifications. Confirm the exact model, current availability and commercial terms with the supplier.</p>}
      {matchingBomItemId && !matchingBomItem && <p className="text-warning">Select a current BOM item to match suppliers.</p>}
      {directoryMode && matchingBomItem && (
        <label className="flex items-center gap-2 text-text-secondary">
          <input type="checkbox" checked={includeCategoryCandidates} onChange={event => setIncludeCategoryCandidates(event.target.checked)} />
          Include category-compatible suppliers (exact equipment unverified)
        </label>
      )}
    </div>
  );

  if (!plan || plan.bom.length === 0) {
    return (
      <div
        className="text-center py-8 space-y-3 border border-dashed border-border"
        data-testid="scm-suppliers-empty"
      >
        <Building2 className="w-10 h-10 text-text-secondary mx-auto" />
        <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
          Suppliers
        </div>
        <p className="type-body text-text-secondary max-w-md mx-auto">
          Seed or import a BoM first — then shortlist suppliers from the global directory or add your own.
        </p>
      </div>
    );
  }

  if (subView === 'directory') {
    return (
      <div className="space-y-4" data-testid="scm-suppliers-browse">
        <div className="flex items-center gap-2 border-b border-border">
          <button
            type="button"
            onClick={() => setSubView('project')}
            className="px-3 py-2 type-meta text-text-secondary hover:text-text-primary"
          >
            Project suppliers ({suppliers.length})
          </button>
          <button
            type="button"
            className="px-3 py-2 type-section border-b-2 border-primary"
          >
            Supplier directory
          </button>
        </div>
        {equipmentFilter(true)}
        <div className="relative max-w-md">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            type="text"
            value={browseSearch}
            onChange={(e) => setBrowseSearch(e.target.value)}
            placeholder="Search global + company directory…"
            className="w-full pl-7 pr-2 py-1.5 bg-background border border-border text-[10px]"
            data-testid="scm-suppliers-browse-search"
          />
        </div>
        {directoryLoading ? (
          <div className="type-body text-text-secondary">Loading directory…</div>
        ) : (
          <ul className="space-y-2 max-h-[32rem] overflow-auto">
            {browseFiltered.length === 0 && <li className="border border-dashed border-border p-3 text-xs text-text-muted">{matchingBomItemId ? 'No recorded equipment matches. Review the selected item or include category-compatible suppliers for discovery.' : 'No suppliers match the current search.'}</li>}
            {browseFiltered.map((s) => {
              const risk = supplierRiskAssessment(s, now);
              const leadLabel = s.typicalLeadMonths
                ? `${s.typicalLeadMonths.low}–${s.typicalLeadMonths.high} mo`
                : 'Lead time —';
              return (
              <li
                key={s.id}
                className="border border-border p-3 bg-background text-[10px]"
                data-testid={`scm-suppliers-browse-row-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{s.name}</div>
                    <div className="type-body text-text-secondary mt-0.5">
                      {s.hq.city ? `${s.hq.city}, ${s.hq.country}` : s.hq.country}
                    </div>
                    <div className="type-body text-text-secondary mt-1">
                      {s.categories.map((c) => SCM_CATEGORY_LABELS[c]).join(' · ')}
                    </div>
                    {matchingBomItem && <p className="mt-2 text-xs text-text-muted" data-testid={`scm-supplier-equipment-evidence-${s.id}`}>
                      {directoryEquipmentMatchIds.has(s.id)
                        ? `Recorded equipment link: ${supplierEquipmentMatchEvidence(plan, matchingBomItem, s, equipmentCatalog).join(' · ')}`
                        : 'Category compatible only; exact equipment is unverified.'}
                    </p>}
                    <div className="flex flex-wrap gap-2 mt-2 type-body text-text-muted">
                      <span>Lead: {leadLabel}</span>
                      {s.capacity && <span>Capacity: {s.capacity}</span>}
                      {s.typicalPricingIndex && <span>Pricing: {s.typicalPricingIndex}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {s.reliabilityTier && (
                        <span className={cn('px-1.5 py-0.5 type-meta font-bold uppercase border', tierTone(s.reliabilityTier))}>
                          {s.reliabilityTier}
                        </span>
                      )}
                      <span className={cn('px-1.5 py-0.5 type-meta font-bold uppercase border', riskTone(risk.overallTier))}>
                        Risk {risk.overallTier}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!onAddFromDirectory || shortlistedIds.has(s.id)}
                    onClick={() => { void onAddFromDirectory?.(s.id); }}
                    className={cn(
                      'shrink-0 px-2 py-1 type-body uppercase tracking-widest border self-start',
                      shortlistedIds.has(s.id)
                        ? 'border-border text-text-secondary cursor-not-allowed'
                        : 'border-primary/40 text-primary hover:bg-primary/10',
                    )}
                    data-testid={`scm-suppliers-browse-add-${s.id}`}
                  >
                    {shortlistedIds.has(s.id) ? 'On project' : 'Add to project'}
                  </button>
                </div>
              </li>
            );})}
          </ul>
        )}
      </div>
    );
  }

  if (suppliers.length === 0) {
    return (
      <div
        className="text-center py-8 space-y-3 border border-dashed border-border"
        data-testid="scm-suppliers-empty"
      >
        <Building2 className="w-10 h-10 text-text-secondary mx-auto" />
        <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
          Suppliers
        </div>
        <p className="type-body text-text-secondary max-w-md mx-auto">
          No project suppliers yet. Browse the supplier directory or add a custom supplier below.
        </p>
        {equipmentFilter()}
        <button
          type="button"
          onClick={() => setSubView('directory')}
          className="inline-flex items-center gap-1 px-3 py-1.5 type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10"
          data-testid="scm-suppliers-browse-cta"
        >
          Open supplier directory
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="scm-suppliers-panel">
      <ScmDataDisclaimer compact />
      {equipmentFilter()}
      {vendorLinkage && (
        <div
          className="text-[10px] text-text-secondary border border-border-subtle bg-surface-1 px-3 py-2 rounded"
          data-testid="scm-reactor-vendor-linkage"
        >
          Reactor vendor <span className="font-bold text-text-primary">{vendorLinkage.label}</span>
          {' '}— {vendorLinkage.supplierIds.length} linked supplier(s) in the platform directory.
        </div>
      )}
      <div className="flex items-center gap-2 border-b border-border">
        <button
          type="button"
          className="px-3 py-2 type-section border-b-2 border-primary"
        >
          Project suppliers ({suppliers.length})
        </button>
        <button
          type="button"
          onClick={() => setSubView('directory')}
          className="px-3 py-2 type-meta text-text-secondary hover:text-text-primary"
          data-testid="scm-suppliers-tab-browse"
        >
          Supplier directory
        </button>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
          Project suppliers — {filtered.length} linked
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-72">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Search by name, HQ, certification..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
              data-testid="scm-suppliers-search"
            />
          </div>
          <input
            value={newSupplierName}
            onChange={(e) => setNewSupplierName(e.target.value)}
            placeholder="Supplier name"
            className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
            data-testid="scm-suppliers-add-name"
          />
          <input
            value={newSupplierCountry}
            onChange={(e) => setNewSupplierCountry(e.target.value)}
            placeholder="HQ country"
            className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
            data-testid="scm-suppliers-add-country"
          />
          <select
            value={newSupplierCategory}
            onChange={(e) => setNewSupplierCategory(e.target.value as SCMCategory)}
            className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
            data-testid="scm-suppliers-add-category"
          >
            {Object.entries(SCM_CATEGORY_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => {
              const name = newSupplierName.trim();
              const country = newSupplierCountry.trim();
              if (!name || !country || !onAddSupplier) return;
              void onAddSupplier({
                name,
                hq: { country },
                categories: [newSupplierCategory],
              });
              setNewSupplierName('');
              setNewSupplierCountry('');
            }}
            disabled={!onAddSupplier || !newSupplierName.trim() || !newSupplierCountry.trim()}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 type-body uppercase tracking-widest border',
              onAddSupplier && newSupplierName.trim() && newSupplierCountry.trim()
                ? 'border-primary/40 text-primary hover:bg-primary/10'
                : 'border-border text-text-secondary cursor-not-allowed',
            )}
            data-testid="scm-suppliers-add-cta"
          >
            <Plus className="w-3 h-3" />
            Add supplier
          </button>
        </div>
      </div>

      {/* SCM13 — compliance chip filter strip. */}
      {allChipIds.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid="scm-suppliers-cert-filters"
          role="group"
          aria-label="Compliance certification filters"
        >
          <button
            type="button"
            onClick={applyBomPreset}
            disabled={bomReqIds.length === 0}
            title={
              bomReqIds.length === 0
                ? 'Seed a BoM with compliance requirements to enable this preset.'
                : `Select every certification (${bomReqIds.length}) referenced in the BoM`
            }
            className={cn(
              'px-2 py-1 type-body font-bold uppercase tracking-widest border transition-colors',
              bomReqIds.length === 0
                ? 'border-dashed border-border text-text-muted cursor-not-allowed'
                : 'border-primary text-primary hover:bg-primary/10',
            )}
            data-testid="scm-suppliers-bom-preset"
          >
            Match BoM requirements
          </button>
          {visibleChipIds.map((id) => {
            const active = activeFilters.has(id);
            const count = counts.get(id) ?? 0;
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleFilter(id)}
                aria-pressed={active}
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-1 type-body uppercase tracking-widest border transition-colors',
                  active
                    ? 'bg-primary/15 border-primary text-primary'
                    : 'bg-background border-border text-text-secondary hover:bg-muted/40',
                )}
                data-testid={`scm-suppliers-cert-chip-${id}`}
              >
                <ShieldCheck className="w-2.5 h-2.5" />
                {certLabel(id)}
                <span className="text-text-muted tabular-nums">{count}</span>
              </button>
            );
          })}
          {allChipIds.length > primaryChipIds.length && (
            <button
              type="button"
              onClick={() => setShowAllChips((v) => !v)}
              className="px-1.5 py-1 type-body uppercase tracking-widest text-text-secondary hover:text-text-primary"
              data-testid="scm-suppliers-cert-show-all"
            >
              {showAllChips
                ? `Show less`
                : `+${allChipIds.length - primaryChipIds.length} more`}
            </button>
          )}
          {activeFilters.size > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-1.5 py-1 type-body uppercase tracking-widest text-text-secondary hover:text-text-primary border border-dashed border-border"
              data-testid="scm-suppliers-cert-clear"
              aria-label="Clear compliance filters"
            >
              <X className="w-2.5 h-2.5" />
              Clear
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
        <ul className="space-y-2" data-testid="scm-suppliers-list">
          {filtered.length === 0 ? (
            <li
              className="text-center py-6 border border-dashed border-border type-body text-text-secondary"
              data-testid="scm-suppliers-no-matches"
            >
              No suppliers match the current filters.
            </li>
          ) : (
            filtered.map((s) => {
              const active = s.id === selectedId;
              const risk = riskById.get(s.id);
              const popoverOpen = openRiskPopoverId === s.id;
              return (
                <li key={s.id} className="relative">
                  <button
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    className={cn(
                      'w-full text-left border p-3 hover:bg-muted/40 transition-colors',
                      active ? 'border-primary bg-primary/5' : 'border-border bg-background',
                    )}
                    data-testid={`scm-supplier-card-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold text-foreground flex items-center gap-1 flex-wrap">
                          {s.name}
                          <SupplierPartnershipBadge supplier={s} />
                          <SupplierStaleBadge supplier={s} />
                        </div>
                        <div className="type-body text-text-secondary mt-0.5">
                          {s.hq.city ? `${s.hq.city}, ${s.hq.country}` : s.hq.country}
                        </div>
                      </div>
                      {s.reliabilityTier && (
                        <span
                          className={cn(
                            'px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest border',
                            tierTone(s.reliabilityTier),
                          )}
                        >
                          {s.reliabilityTier}
                        </span>
                      )}
                    </div>
                    <div className="type-body text-text-secondary mt-2">
                      {s.categories.map((c) => SCM_CATEGORY_LABELS[c]).join(' · ')}
                    </div>
                    {matchingBomItem && <p className="mt-2 text-xs text-text-muted" data-testid={`scm-supplier-equipment-evidence-${s.id}`}>Recorded equipment link: {supplierEquipmentMatchEvidence(plan, matchingBomItem, s, equipmentCatalog).join(' · ')}</p>}
                    {s.certifications && s.certifications.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {s.certifications.map((c) => {
                          const rawId = normaliseCertification(c).id;
                          const id = normalizeComplianceId(rawId);
                          const isMatch = activeFilters.has(id);
                          return (
                            <span
                              key={rawId}
                              className={cn(
                                'inline-flex items-center gap-1 px-1.5 py-0.5 border type-meta uppercase tracking-widest',
                                isMatch
                                  ? 'bg-primary/15 border-primary text-primary'
                                  : 'bg-muted border-border text-text-secondary',
                              )}
                            >
                              <ShieldCheck className="w-2.5 h-2.5" />
                              {certLabel(id)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </button>
                  {/* SCM14 risk pill — positioned absolutely so it lives OUTSIDE
                      the card button (avoids invalid nested <button>). */}
                  {risk && (
                    <button
                      type="button"
                      onClick={() => setOpenRiskPopoverId(popoverOpen ? null : s.id)}
                      aria-expanded={popoverOpen}
                      aria-label={`Risk ${risk.overallTier} — open breakdown`}
                      className={cn(
                        'absolute top-[2.05rem] right-3 inline-flex items-center gap-1 px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest border cursor-pointer z-[5]',
                        riskTone(risk.overallTier),
                      )}
                      data-testid={`scm-supplier-risk-pill-${s.id}`}
                      data-risk={risk.overallTier}
                    >
                      {(risk.overallTier === 'high' || risk.overallTier === 'critical') && (
                        <AlertTriangle className="w-2.5 h-2.5" />
                      )}
                      Risk {risk.overallTier}
                    </button>
                  )}
                  {popoverOpen && risk && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setOpenRiskPopoverId(null)}
                        data-testid={`scm-supplier-risk-backdrop-${s.id}`}
                      />
                      <div
                        role="dialog"
                        aria-label={`${s.name} risk breakdown`}
                        className="absolute right-0 top-full mt-1 z-20 w-72 border border-border bg-background shadow-lg p-3 space-y-2"
                        data-testid={`scm-supplier-risk-popover-${s.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
                            Risk breakdown
                          </div>
                          <span
                            className={cn(
                              'px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest border',
                              riskTone(risk.overallTier),
                            )}
                          >
                            {risk.overallTier}
                          </span>
                        </div>
                        <ul className="space-y-1 type-body text-foreground">
                          <li data-testid={`scm-supplier-risk-geo-${s.id}`}>
                            {geopoliticalLine(risk, s.hq.country)}
                          </li>
                          <li data-testid={`scm-supplier-risk-certs-${s.id}`}>
                            {certLine(risk)}
                          </li>
                          <li data-testid={`scm-supplier-risk-fin-${s.id}`}>
                            {financialLine(risk)}
                          </li>
                        </ul>
                      </div>
                    </>
                  )}
                </li>
              );
            })
          )}
        </ul>

        <aside
          className="border border-border bg-background p-4 min-h-[10rem]"
          data-testid="scm-supplier-detail"
        >
          {selected ? (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] font-bold text-foreground flex items-center gap-1 flex-wrap">
                  {selected.name}
                  <SupplierPartnershipBadge supplier={selected} />
                </div>
                <div className="type-body text-text-secondary mt-0.5">
                  {selected.hq.city
                    ? `${selected.hq.city}, ${selected.hq.country}`
                    : selected.hq.country}
                </div>
              </div>
              {selectedRisk && (
                <div className="space-y-1" data-testid="scm-supplier-detail-risk">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest border',
                        riskTone(selectedRisk.overallTier),
                      )}
                    >
                      Risk {selectedRisk.overallTier}
                    </span>
                    <span className="type-body text-text-secondary">
                      worst-of-three
                    </span>
                  </div>
                  <ul className="type-body text-text-secondary list-disc pl-4 space-y-0.5">
                    <li>{geopoliticalLine(selectedRisk, selected.hq.country)}</li>
                    <li>{certLine(selectedRisk)}</li>
                    <li>{financialLine(selectedRisk)}</li>
                  </ul>
                </div>
              )}
              {selected.typicalLeadMonths && (
                <div className="type-body text-text-secondary">
                  Typical lead — {selected.typicalLeadMonths.low}-{selected.typicalLeadMonths.high} mo
                </div>
              )}
              {selected.notes && (
                <p className="type-body text-text-secondary border-l-2 border-border pl-2">
                  {selected.notes}
                </p>
              )}
              {onEmailSupplier && (
                <button
                  type="button"
                  onClick={() => onEmailSupplier(selected)}
                  className="px-2 py-1 type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10"
                  data-testid={`scm-supplier-email-${selected.id}`}
                >
                  Email supplier
                </button>
              )}
              <div className="pt-2 border-t border-border space-y-1">
                <div className="type-body text-text-secondary">
                  Shortlisted on{' '}
                  <span className="text-foreground font-bold">
                    {shortlistedItems.length}
                  </span>{' '}
                  BoM item{shortlistedItems.length === 1 ? '' : 's'}.
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <select
                    value={attachBomItemId}
                    onChange={(e) => setAttachBomItemId(e.target.value)}
                    className="px-2 py-1 bg-background border border-border type-body text-foreground"
                    data-testid="scm-supplier-attach-bom-select"
                  >
                    <option value="">Attach to BoM item...</option>
                    {(plan?.bom ?? []).map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      if (!onAttachSupplierToBomItem || !selected || !attachBomItemId) return;
                      void onAttachSupplierToBomItem({
                        supplierId: selected.id,
                        bomItemId: attachBomItemId,
                      });
                      setAttachBomItemId('');
                    }}
                    disabled={!onAttachSupplierToBomItem || !attachBomItemId}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-1 type-body font-bold uppercase tracking-widest border',
                      onAttachSupplierToBomItem && attachBomItemId
                        ? 'border-primary/40 text-primary hover:bg-primary/10'
                        : 'border-border text-text-secondary cursor-not-allowed',
                    )}
                    data-testid="scm-supplier-attach-cta"
                  >
                    <Link2 className="w-2.5 h-2.5" />
                    Attach to item
                  </button>
                </div>
                {shortlistedItems.length > 0 && (
                  <ul className="type-body text-text-secondary list-disc pl-4 space-y-0.5">
                    {shortlistedItems.slice(0, 4).map((b) => <li key={b.id}>{b.name}</li>)}
                  </ul>
                )}
              </div>
            </div>
          ) : (
            <div className="type-body text-text-secondary text-center py-6">
              Select a supplier to see lead-time + quote history.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};
